export type ConsentValue = 'granted' | 'denied'

const EVENT_NAME = 'judahs-cookie-consent-change'

/**
 * 동의 쿠키. judahs.io 와 forum.judahs.io 가 **같은 선택을 공유**하도록 `.judahs.io` 에 쓴다
 * (2026-09-15, PENDING_CHANGES §30). 짝은 forum-theme `jd-analytics.js` — 이름·값·도메인을 같이 바꾼다.
 * 선택 자체를 기억하는 쿠키라 필수 쿠키다. 13개월(395일)이 지나면 다시 묻는다.
 */
const COOKIE_NAME = 'judahs_cookie_consent'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 395
const SHARED_DOMAIN = 'judahs.io'

/** 2026-09-15 이전에는 localStorage 에 두었다. 읽기만 하고 쿠키로 옮긴 뒤 지운다 */
const LEGACY_STORAGE_KEY = 'judahs_cookie_consent'

const isConsentValue = (value: string | null): value is ConsentValue =>
	value === 'granted' || value === 'denied'

/** localhost 에서는 도메인을 붙이지 않는다 — 붙이면 브라우저가 쿠키를 버린다 */
const cookieDomainPart = (): string => {
	const host = window.location.hostname
	return host === SHARED_DOMAIN || host.endsWith(`.${SHARED_DOMAIN}`)
		? `; domain=.${SHARED_DOMAIN}`
		: ''
}

const readCookie = (): ConsentValue | null => {
	const pair = document.cookie
		.split(';')
		.map(aPair => aPair.trim())
		.find(aPair => aPair.startsWith(`${COOKIE_NAME}=`))
	const value = pair === undefined ? null : pair.slice(COOKIE_NAME.length + 1)
	return isConsentValue(value) ? value : null
}

const writeCookie = (value: ConsentValue | null) => {
	if (value === null) {
		document.cookie = `${COOKIE_NAME}=; Max-Age=0; path=/${cookieDomainPart()}`
		return
	}
	const secure = window.location.protocol === 'https:' ? '; Secure' : ''
	document.cookie = `${COOKIE_NAME}=${value}; Max-Age=${COOKIE_MAX_AGE}; path=/${cookieDomainPart()}; SameSite=Lax${secure}`
}

const takeLegacyValue = (): ConsentValue | null => {
	try {
		const stored = window.localStorage.getItem(LEGACY_STORAGE_KEY)
		window.localStorage.removeItem(LEGACY_STORAGE_KEY)
		return isConsentValue(stored) ? stored : null
	} catch {
		// 프라이빗 모드 등 localStorage 접근 불가
		return null
	}
}

/**
 * 분석 쿠키(Google Analytics) 동의 상태를 보관한다.
 * 동의 전에는 GA 를 초기화하지 않는다 — utils/Analytics 참조.
 */
export const CookieConsent = {
	get: (): ConsentValue | null => {
		const fromCookie = readCookie()
		if (fromCookie !== null) return fromCookie

		const legacy = takeLegacyValue()
		if (legacy !== null) writeCookie(legacy)
		return legacy
	},

	isGranted: (): boolean => CookieConsent.get() === 'granted',

	set: (value: ConsentValue): void => {
		writeCookie(value)
		window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: value }))
	},

	/** 선택을 지워 모달을 다시 띄운다 (푸터의 Cookie Settings). 포럼에도 같이 적용된다 */
	reset: (): void => {
		writeCookie(null)
		takeLegacyValue()
		window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: null }))
	},

	/** 동의 상태가 바뀌면 호출된다. 해제 함수를 반환한다. */
	subscribe: (listener: (value: ConsentValue | null) => void): (() => void) => {
		const handler = () => listener(CookieConsent.get())
		// 쿠키는 바뀌어도 이벤트가 없다 — 다른 탭(포럼 포함)에서 바꾼 선택은 돌아왔을 때 읽는다
		const onVisible = () => {
			if (document.visibilityState === 'visible') handler()
		}

		window.addEventListener(EVENT_NAME, handler)
		window.addEventListener('focus', handler)
		document.addEventListener('visibilitychange', onVisible)

		return () => {
			window.removeEventListener(EVENT_NAME, handler)
			window.removeEventListener('focus', handler)
			document.removeEventListener('visibilitychange', onVisible)
		}
	},
}
